<template>
  <div
    :class="`${bem.b()} ${prepareClassNames()}`"
    :style="{
      ...prepareStyles(),
    }"
  >
    <el-form
      ref="elFormRef"
      :model="{ table: data }"
      size="small"
      hide-required-asterisk
      scroll-to-error
      :scroll-into-view-options="{ inline: 'center' }"
      class="ep-form"
    >
      <el-table ref="elTableRef" v-loading="loading" v-bind="customAttrs">
        <el-table-column
          v-if="showSelectionCol"
          type="selection"
          width="55"
          :align
          :selectable
          :reserve-selection="reserveSelection"
          fixed
        />
        <el-table-column
          v-if="showSingleSelectionCol"
          width="55"
          :align
          :selectable
          fixed
        >
          <template #default="scope">
            <el-link
              type="primary"
              underline="never"
              style="font-size: 12px"
              @click="emit('single-selection-change', scope.row)"
              >{{ t('ep.table.select') }}</el-link
            >
          </template>
        </el-table-column>
        <el-table-column
          v-if="showIndexCol"
          type="index"
          width="56"
          :align
          :label="t('ep.table.serialNumber')"
          fixed
          :index="indexFormatter"
        />

        <el-table-column
          v-if="actionButtons.length"
          :label="t('ep.table.operation')"
          :align
          fixed
          :width="actionColWidth"
        >
          <template #default="scope">
            <span :class="bem.e('action-buttons')">
              <EpButtons
                :list="formatActionButtons(actionButtons, scope)"
                type="text"
              />
            </span>
          </template>
        </el-table-column>
        <el-table-column
          v-for="(item, index) in columnList"
          :key="`${item.prop}_${item.type}_${index}`"
          v-bind="item"
        >
          <template #default="scope">
            <EpFormItem
              v-if="item.type || item.render"
              :form-data="scope.row"
              :table-scope="scope"
              v-bind="formItemProps(scope, item)"
            />
            <template v-else>
              {{ scope.row[item.prop] }}
            </template>
          </template>
        </el-table-column>
        <template v-for="name in slots" #[name]="scope">
          <slot :name="name" v-bind="scope" />
        </template>
      </el-table>
    </el-form>
    <el-pagination
      v-if="showPagination"
      v-model:current-page="pagination.pageNo"
      v-model:page-size="pagination.pageSize"
      :total="pagination.total"
      v-bind="paginationProps"
    />
    <EpCustomColumn
      v-if="customColumnModule"
      ref="customColumnRef"
      :module="customColumnModule"
      :api="customColumnApi"
      :save-api="customColumnSaveApi"
      :show-handler="false"
    />
  </div>
</template>
<script setup lang="tsx">
import { computed, useSlots, useTemplateRef } from 'vue'
import { createNameSpace } from '@el-plus/utils/bem'
import {
  tableProps,
  tableEmits,
  expandTablePropsKeys,
  tableEmitsKeys,
} from './table'
import type { TableEmits } from './table'
import {
  prepareProps,
  prepareEvents,
  prepareClassNames,
  prepareStyles,
} from '@el-plus/utils/props'
import { EpFormItem } from '@el-plus/components/form'
import EpCustomColumn from '@el-plus/components/custom-column'
import type { CustomColumnInstance } from '@el-plus/components/custom-column'
import { useTable, useTableColumn, useTableFormItem } from './use-table'
import { useLocale } from '@el-plus/hooks/use-locale'
const { t } = useLocale()
defineOptions({
  name: 'EpTable',
  inheritAttrs: false,
})
const bem = createNameSpace('table')
const props = defineProps(tableProps)
const emit = defineEmits(tableEmits!)

const slots = computed(() => {
  return Object.keys(useSlots())
  // .filter((item) => item !== 'default')
})
const customAttrs = computed(() => {
  return {
    'row-key': props.rowKey || 'id',
    ...prepareProps(props, ['modelValue', ...expandTablePropsKeys]),
    // 'tooltip-effect': 'light',
    size: 'small',
    data: data.value,
    ...prepareEvents<NonNullable<TableEmits>>(emit, tableEmitsKeys!),
  }
})
const {
  pagination,
  paginationProps,
  data,
  validate,
  resetFields,
  clearValidate,
  clearSelection,
  getSelectionRows,
  loading,
  search,
  elFormRef,
  elTableRef,
  formatActionButtons,
  customColumns,
} = useTable(props)
const { columnList } = useTableColumn(props, data, customColumns)
const { formItemProps } = useTableFormItem(props)
const customColumnRef = useTemplateRef<CustomColumnInstance>('customColumnRef')

defineExpose({
  elFormRef,
  elTableRef,
  validate,
  resetFields,
  clearValidate,
  clearSelection,
  getSelectionRows,
  search,
  openCustomColumnDialog: () => {
    customColumnRef.value?.open()
  },
})
</script>
